import * as React from 'react';
import { Dispatch, connect } from "react-redux";
import { Panel, FormControl, FormGroup, Alert, ControlLabel } from 'react-bootstrap';
import { IBillingDetailResponse, IBillingState, IApplicationState, AppActionCreators, BillingActionCreators, ISaveBillingDetails, IAppState } from '@nkr/store/index';
import { Utility } from '@nkr/services/utility';
import UploadFile from '../view-edit/upload-file';
import DirectDepositInformation from '../direct-deposit-information';
import { IFile } from '../view-edit/file';
import { FileType } from '../view-edit/file-type';
import { AmountWrapper, RowWrapper } from '../styles';
import NavRecipientCenter from '../nav-recipient-center';
import { NavSubWrapper } from '../../styles';
import { BillableNephrectomyStatus } from '@nkr/enums/billable-nephrectomy-status';

interface IOwnProps {
    donorId: number,
    centerId: number,
    centerAlias: string,
    section: string,
    tabText: string,
    onLoaded: (tabText: string) => void
}

interface IStateProps {
    billing: IBillingState,
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDetails: (donorId: number, centerId: number) => Promise<IBillingDetailResponse>,
    saveDetails: (details: ISaveBillingDetails) => Promise<IBillingDetailResponse>,
    saveAch: (donorCenterId: number, donorId: number, centerRoutingNumber: string, centerAccountNumber: string, centerW9File: File) => Promise<IBillingDetailResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    physicianCharges: string,
    anesthesiaCharges: string,
    hospitalCharges: string,
    centerRoutingNumber: string,
    centerAccountNumber: string,
    hospitalW9Form: IFile,
    existingHosptialW9File: string,
    valid: {
        physicianCharges: boolean,
        anesthesiaCharges: boolean,
        hospitalCharges: boolean,
        atLeastOneItemSpecified: boolean
    },
    dirty: boolean
}

class PayThroughNkr extends React.Component<TProps, IState> {
    readonly state: IState = {
        physicianCharges: '',
        anesthesiaCharges: '',
        hospitalCharges: '',
        centerRoutingNumber: '',
        centerAccountNumber: '',
        hospitalW9Form: null,
        existingHosptialW9File: '',
        valid: {
            physicianCharges: true,
            anesthesiaCharges: true,
            hospitalCharges: true,
            atLeastOneItemSpecified: false
        },
        dirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let { donorId, centerId } = this.props;

        this.props.showLoading("Getting billing details, one moment please...").then(() => {
            this.props.getDetails(donorId, centerId).then((response: IBillingDetailResponse) => {
                this.props.hideLoading();

                if (response.data.success) {
                    let { physicianCharges, anesthesiaCharges, hospitalCharges, centerRoutingNumber, centerAccountNumber, isBillSent, billableNephrectomy } = response.data.results;

                    this.setState({
                        physicianCharges: physicianCharges > 0 ? physicianCharges.toString() : '',
                        anesthesiaCharges: anesthesiaCharges > 0 ? anesthesiaCharges.toString() : '',
                        hospitalCharges: hospitalCharges > 0 ? hospitalCharges.toString() : '',
                        centerRoutingNumber,
                        centerAccountNumber
                    });

                    let { chainId, clusterNumber, positionNumber } = response.data.results.billableNephrectomy;

                    let tabText = `Billing Request for Swap ${chainId}/${clusterNumber}/${positionNumber}`;

                    if (billableNephrectomy.statusId == BillableNephrectomyStatus.SubmittedByRecipientCenter) {
                        tabText = `Submitted Billing Request for Swap ${chainId}/${clusterNumber}/${positionNumber}`;
                    }

                    this.props.onLoaded(tabText);
                }
            });
        });
    }

    handlePhysicianChargeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let valid = { ...this.state.valid };

        if (this.state.dirty) {
            valid.physicianCharges = e.currentTarget.value != '' ? Utility.isNumber(e.currentTarget.value) : true;
        }

        this.setState({
            physicianCharges: e.currentTarget.value,
            valid
        });
    }

    handleAnesthesiaChargeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let valid = { ...this.state.valid };

        if (this.state.dirty) {
            valid.anesthesiaCharges = e.currentTarget.value != '' ? Utility.isNumber(e.currentTarget.value) : true;
        }

        this.setState({
            anesthesiaCharges: e.currentTarget.value,
            valid
        });
    }

    handleHospitalChargeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let valid = { ...this.state.valid };

        if (this.state.dirty) {
            valid.hospitalCharges = e.currentTarget.value != '' ? Utility.isNumber(e.currentTarget.value) : true;
        }

        this.setState({
            hospitalCharges: e.currentTarget.value,
            valid
        });
    }

    handleCenterRoutingNumberChange = (value: string) => {
        this.setState({
            centerRoutingNumber: value
        });
    }

    handleCenterAccountNumberChange = (value: string) => {
        this.setState({
            centerAccountNumber: value
        });
    }

    handleHospitalW9Form = (file: File) => {
        let f: IFile = {
            type: FileType.HospitalW9Form,
            file
        };

        this.setState({
            hospitalW9Form: f
        });
    }

    handleSave = () => {
        let valid = this.validate();

        this.setState({
            valid,
            dirty: true
        }, () => {
            let { physicianCharges, anesthesiaCharges, hospitalCharges, atLeastOneItemSpecified } = valid;

            if (physicianCharges && anesthesiaCharges && hospitalCharges && atLeastOneItemSpecified) {
                let billableNephrectomy = this.props.billing.details.billableNephrectomy;
                let { donor, recipient } = billableNephrectomy;

                var details: ISaveBillingDetails = {
                    donorCenterId: donor.center.id,
                    donorId: donor.id,
                    centerRoutingNumber: '',
                    centerAccountNumber: '',
                    centerW9File: null,
                    recipientId: recipient.id,
                    physicianCharges: parseFloat(this.state.physicianCharges),
                    physicianChargesFile: null,
                    anesthesiaCharges: parseFloat(this.state.anesthesiaCharges),
                    anesthesiaChargesFile: null,
                    hospitalCharges: parseFloat(this.state.hospitalCharges),
                    hospitalChargesFile: null,
                    asaUnitCount: 0,
                    isRecipientCenter: true,
                    accept: false
                }

                this.props.showLoading("Saving the payment information, one moment please...").then(() => {
                    this.props.saveDetails(details).then((response: IBillingDetailResponse) => {
                        this.props.hideLoading();
                    });
                });
            }
        });
    }

    handleSaveACH = () => {
        let billableNephrectomy = this.props.billing.details.billableNephrectomy;
        let { donor } = billableNephrectomy;
        let { centerRoutingNumber, centerAccountNumber, hospitalW9Form } = this.state;
        let file = hospitalW9Form != null ? hospitalW9Form.file : null;

        this.props.showLoading("Saving the ACH information, one moment please...").then(() => {
            this.props.saveAch(donor.center.id, donor.id, centerRoutingNumber, centerAccountNumber, file).then((response: IBillingDetailResponse) => {
                this.props.hideLoading();
            });
        });
    }

    handleCancel = () => {
        if (this.props.billing.details.billableNephrectomy.id == 0) {
            window.location.href = `/center/${this.props.centerId}/billing/pay-through-nkr`;
        } else {
            window.location.href = this.props.billing.details.isDonorCenter ? `/center/${this.props.centerId}/billing/payment-submitted-to` : `/center/${this.props.centerId}/billing/payment-submitted-by`;
        }
    }

    validate = () => {
        let { physicianCharges, anesthesiaCharges, hospitalCharges } = this.state;
        let valid = { ...this.state.valid };

        valid.physicianCharges = physicianCharges != '' ? Utility.isNumber(physicianCharges) : true;
        valid.anesthesiaCharges = anesthesiaCharges != '' ? Utility.isNumber(anesthesiaCharges) : true;
        valid.hospitalCharges = hospitalCharges != '' ? Utility.isNumber(hospitalCharges) : true;
        valid.atLeastOneItemSpecified = physicianCharges != '' || anesthesiaCharges != '' || hospitalCharges != '';

        return valid;
    }

    getTotal = () => {
        let { physicianCharges, anesthesiaCharges, hospitalCharges } = this.state;

        let totalCharges = (Utility.isNumber(physicianCharges) ? parseFloat(physicianCharges) : 0)
            + (Utility.isNumber(anesthesiaCharges) ? parseFloat(anesthesiaCharges) : 0)
            + (Utility.isNumber(hospitalCharges) ? parseFloat(hospitalCharges) : 0);

        return totalCharges;
    }

    getAlertMessage = (): { message: string | JSX.Element, style: string } => {
        const { physicianCharges, anesthesiaCharges, hospitalCharges, atLeastOneItemSpecified } = this.state.valid;
        const validCharges = physicianCharges && anesthesiaCharges && hospitalCharges;

        if (this.props.billing.details.isBillSent && !this.props.billing.details.isDonorCenter) {
            return { message: 'This billing request will appear on your next NKR invoice.', style: 'info' };
        }

        if (this.props.billing.details.isBillSent && this.props.billing.details.isDonorCenter) {
            if (!this.props.billing.details.achSaved) {
                return { message: `This billing request was submitted by ${this.props.billing.details.billableNephrectomy.recipient.center.alias}.`, style: 'info' };
            } else {
                return {
                    message:
                        <>
                            <div style={{ marginBottom: 20, fontWeight: 700 }}>ACH information saved successfully.</div>
                            <div>This billing request was submitted by {this.props.billing.details.billableNephrectomy.recipient.center.alias}.</div>
                        </>,
                    style: 'info'
                };
            }
        }

        if (!validCharges && this.state.dirty) {
            return {
                message:
                    <>
                        <div>The items outlined in red must be either:</div>
                        <div style={{ marginLeft: 20 }}>&middot; empty (no charge amount)</div>
                        <div style={{ marginLeft: 20 }}>&middot; or a valid dollar amount without a dollar sign nor a comma, i.e. 16,000...</div>
                    </>,
                style: 'danger'
            };
        }

        if (!atLeastOneItemSpecified && this.state.dirty) {
            return { message: 'Please specify an amount for at least one item...', style: 'danger' };
        }

        return null;
    }

    render = () => {
        if (this.props.billing.details == null)
            return null;

        const { details } = this.props.billing;
        const { donor, recipient, chainId, clusterNumber, positionNumber } = details.billableNephrectomy;
        const totalCharges = this.getTotal();
        const disableSubmitButton = false;
        const alertMessage = this.getAlertMessage();

        return (
            <div style={{ marginBottom: 10, borderWidth: '0px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderBottomLeftRadius: 4, borderBottomRightRadius: 4, padding: '0px 5px 0px 5px' }}>
                <NavSubWrapper verticalAlign='center' minTabWidth='85' fontSize='12px'>
                    <NavRecipientCenter
                        section={this.props.section}
                        tabText={this.props.tabText}
                        centerId={this.props.centerId}
                        centerAlias={this.props.centerAlias}
                    />
                </NavSubWrapper>

                <div className="container">
                    {alertMessage != null && (
                        <div className="row" style={{ marginTop: 10 }}>
                            <div className="col-xs-12">
                                <Alert bsStyle={alertMessage.style}>{alertMessage.message}</Alert>
                            </div>
                        </div>
                    )}

                    <div className="row row-margined">
                        <div className="col-xs-12">
                            <Panel>
                                <Panel.Heading style={{ fontSize: 16 }}>
                                    Swap Information ({chainId}/{clusterNumber}/{positionNumber})
                                </Panel.Heading>

                                <Panel.Body>
                                    <div className="row not-striped">
                                        <div className='col-xs-2' style={{ width: "13%", fontWeight: 700 }}>Recipient Center:</div>
                                        <div className='col-xs-2'>{recipient.center.alias}</div>
                                        <div className='col-xs-2' style={{ width: "10%", fontWeight: 700 }}>Recipient:</div>
                                        <div className='col-xs-2'>{recipient.alias}</div>
                                        <div className='col-xs-2' style={{ width: "15%", fontWeight: 700 }}>Recipient Insurance:</div>
                                        <div className='col-xs-2'>{recipient.insurance.primaryGroupName}</div>
                                    </div>
                                    <div className="row not-striped">
                                        <div className='col-xs-2' style={{ width: "13%", fontWeight: 700 }}>Donor Center:</div>
                                        <div className='col-xs-2'>{donor.center.alias} {donor.rdnCenterId > 0 ? <span style={{ color: 'darkred' }}>({donor.rdnCenterAlias})</span> : null}</div>
                                        <div className='col-xs-2' style={{ width: "10%", fontWeight: 700 }}>Donor:</div>
                                        <div className='col-xs-2'>{donor.alias}</div>
                                    </div>
                                    <div className="row not-striped">
                                        <div className='col-xs-2' style={{ width: "13%", fontWeight: 700 }}>SBS Swap: </div>
                                        <div className='col-xs-2'>{details.isSbs ? "Yes" : "No"}</div>
                                        <div className='col-xs-2' style={{ width: "10%", fontWeight: 700 }}>Global: </div>
                                        <div className='col-xs-2'>{details.isGlobal ? "Yes" : "No"}</div>
                                    </div>
                                    <div className="row not-striped">
                                        <div className='col-xs-2' style={{ width: "13%", fontWeight: 700 }}>Transplant Date: </div>
                                        <div className='col-xs-10'>{recipient.transplantDate}</div>
                                    </div>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </div>

                    <Panel>
                        <Panel.Heading style={{ fontSize: 16 }}>
                            Charge Information
                        </Panel.Heading>

                        <Panel.Body>
                            <RowWrapper className="row row-margined">
                                <div className='col-xs-12 col-lg-3'><strong>Item</strong></div>
                                <div className='col-xs-12 col-lg-2 col-lg-offset-1'><strong>Amount</strong></div>
                            </RowWrapper>

                            <RowWrapper className="row row-margined">
                                <div className='col-xs-12 col-lg-3'>Physician Donor Nephrectomy</div>
                                <div className='col-xs-12 col-lg-2 col-lg-offset-1'>
                                    <FormGroup style={{ marginBottom: 0 }} validationState={!this.state.valid.physicianCharges ? 'error' : null}>
                                        <AmountWrapper
                                            value={this.state.physicianCharges}
                                            disabled={this.props.billing.details.isDonorCenter}
                                            onChange={this.handlePhysicianChargeChange}
                                        />
                                    </FormGroup>
                                </div>
                            </RowWrapper>

                            <RowWrapper className="row row-margined">
                                <div className='col-xs-12 col-lg-3'>Anesthesia</div>
                                <div className='col-xs-12 col-lg-2 col-lg-offset-1'>
                                    <FormGroup style={{ marginBottom: 0 }} validationState={!this.state.valid.anesthesiaCharges ? 'error' : null}>
                                        <AmountWrapper
                                            value={this.state.anesthesiaCharges}
                                            disabled={this.props.billing.details.isDonorCenter}
                                            onChange={this.handleAnesthesiaChargeChange}
                                        />
                                    </FormGroup>
                                </div>
                            </RowWrapper>

                            <RowWrapper className="row row-margined">
                                <div className='col-xs-12 col-lg-3'>Hospital Donor Nephrectomy</div>
                                <div className='col-xs-12 col-lg-2 col-lg-offset-1'>
                                    <FormGroup style={{ marginBottom: 0 }} validationState={!this.state.valid.hospitalCharges ? 'error' : null}>
                                        <AmountWrapper
                                            value={this.state.hospitalCharges}
                                            disabled={this.props.billing.details.isDonorCenter}
                                            onChange={this.handleHospitalChargeChange}
                                        />
                                    </FormGroup>
                                </div>
                            </RowWrapper>

                            <RowWrapper className="row row-margined" style={{ height: 34 }}>
                                <div className='col-xs-12 col-lg-3'>Total to be Paid</div>
                                <div className='col-xs-12 col-lg-2 col-lg-offset-1'>${totalCharges.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </RowWrapper>
                        </Panel.Body>
                    </Panel>

                    {this.props.billing.details.isDonorCenter && (
                        <DirectDepositInformation
                            details={this.props.billing.details}
                            centerRoutingNumber={this.state.centerRoutingNumber}
                            centerAccountNumber={this.state.centerAccountNumber}
                            hospitalW9Form={this.state.hospitalW9Form}
                            wasSubmittedByRecipientCenter={true}
                            isAdmin={this.props.app.user.group.isSuper}
                            onChangeCenterRoutingNumber={this.handleCenterRoutingNumberChange}
                            onChangeCenterAccountNumber={this.handleCenterAccountNumberChange}
                            onHospitalW9Form={this.handleHospitalW9Form}
                        />
                    )}

                    {!details.isBillSent ?
                        <div style={{ display: "flex", justifyContent: "center", marginTop: 30, marginBottom: 20 }}>
                            <button className="btn btn-primary btn-lg" onClick={this.handleSave} disabled={disableSubmitButton}>SUBMIT FOR PAYMENT</button>
                            <button className="btn btn-link" onClick={this.handleCancel}>Cancel &amp; Return to the List</button>
                        </div>
                        :
                        <>
                            {details.isDonorCenter ?
                                <div style={{ display: "flex", justifyContent: "center", marginTop: 10, marginBottom: 10 }}>
                                    <button className="btn btn-primary btn-lg" onClick={this.handleSaveACH} disabled={disableSubmitButton}>SAVE ACH</button>
                                    <button className="btn btn-link btn-lg" onClick={this.handleCancel}>Return to the List</button>
                                </div>
                                :
                                <div style={{ display: "flex", justifyContent: "center", marginTop: 10, marginBottom: 10 }}>
                                    <button className="btn btn-primary btn-lg" onClick={this.handleCancel}>Return to the List</button>
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        billing: appState.billing,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { dispatch(AppActionCreators.hideLoading()); },
        getDetails: (donorId, centerId) => { return dispatch(BillingActionCreators.getDetails(donorId, centerId)); },
        saveDetails: (details) => { return dispatch(BillingActionCreators.saveDetails(details)); },
        saveAch: (donorCenterId, donorId, centerRoutingNumber, centerAccountNumber, centerW9File) => { return dispatch(BillingActionCreators.saveAch(donorCenterId, donorId, centerRoutingNumber, centerAccountNumber, centerW9File)); }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(PayThroughNkr);